import { transparentize } from 'polished'
import { focus } from '../../helpers'
import { Button, buttonVariant } from '../Button'
import { StyleFn } from '..'

export const Tabs: StyleFn = () => ({
  root: {
    // reset
    margin: 0,
    padding: 0,
    listStyleType: 'none',
    // layout
    display: 'inline-flex',
    whiteSpace: 'nowrap',
    marginBottom: '1px',
    // no bottom border on child tabs
    '&$_floating li:before, &$_floating a:before': {
      borderBottom: 'none',
    },
    '&$_floating li:after, &$_floating a:after': {
      borderWidth: 1,
    },
  },
  _floating: {
    marginBottom: 0,
    // floating tabs stretch to full height
    alignSelf: 'stretch',
  },
  scrollwrapper: {
    display: 'flex',
    alignSelf: 'stretch',
    overflow: 'hidden',
    maskImage: 'linear-gradient(to right, black 95%, transparent 100%)',
    '& $root': {
      overflow: 'hidden',
      overflowX: 'scroll',
      flex: '1 1 auto',
      scrollbarWidth: 'none',
      '&::-webkit-scrollbar': {
        display: 'none',
      },
    },
  },
})

export const Tab: StyleFn = (theme) => {
  const { color, font, size } = theme
  const { root: rootButtonStyles, _medium, _large, _xlarge } = Button(theme)
  const { focusWithPseudo } = focus(theme)

  // TODO group all color values here in a `colors` object
  const borderColor = color.getModalValues(color.border.tab)
  const containerSelectedBGColor =
    color.type === 'light' ? color.palette.white : transparentize(0.92, color.palette.white)

  return {
    root: {
      extend: [
        rootButtonStyles,
        buttonVariant(theme, 'ghost'),
        focusWithPseudo({
          pseudo: 'before',
          radius: '0',
          offset: 1,
        }),
      ],
      borderRadius: 0,
      display: 'inline-flex',
      minWidth: 'initial',
      fontWeight: font.weight.medium,
      height: '100%',
      position: 'relative',
      padding: `${size.spacing[8]} ${size.spacing[16]}`,
      '&:before': {
        content: '""',
        position: 'absolute',
        left: -1,
        right: -1,
        bottom: -1,
        borderBottom: `1px solid ${color.getModalValues(color.border.interactive)}`,
      },
    },
    _medium,
    _large,
    _xlarge,
    _container: {
      // light: warmgrey-30 - dark: white/16%
      backgroundColor: color.type === 'light' ? color.palette.warmgrey[30] : transparentize(0.84, color.palette.white),
      // light: warmgrey-60 - dark: white/50%
      '&:before': {
        borderColor: color.type === 'light' ? color.palette.warmgrey[60] : transparentize(0.5, color.palette.white),
      },
      '&:hover': {
        // light: warmgrey-40 - dark: white/24%
        backgroundColor:
          color.type === 'light' ? color.palette.warmgrey[40] : transparentize(0.76, color.palette.white),
      },
      '&$_selected': {
        // light: white - dark: white/8%
        backgroundColor: containerSelectedBGColor,
        '&:hover': { backgroundColor: containerSelectedBGColor },
        '&:before': { display: 'none' },
        '&:focus-visible:before': { display: 'block' },
        '&:after': {
          top: -1,
          bottom: 'unset',
        },
      },
    },
    _selected: {
      fontWeight: font.weight.bold,
      '&:after': {
        content: '""',
        position: 'absolute',
        left: -1,
        right: -1,
        bottom: -1,
        border: `2px solid ${borderColor}`,
      },
      '&:hover': {
        cursor: 'unset',
        backgroundColor: color.type === 'light' ? color.palette.white : 'transparent',
      },
    },
    _disabled: {
      pointerEvents: 'none',
      color: transparentize(
        0.6,
        color.type === 'light'
          ? color.background.interactive.ghost.contrastText
          : color.background.interactive.ghostReverse.contrastText,
      ),
    },
  }
}
